import React from 'react';
import { Link, type To } from 'react-router-dom';
import { Clock, DatabaseStar, MultiBubble, Search, StatsReport, Tools, Puzzle, Settings } from 'iconoir-react';
import { cn } from 'lib/utils';
import { sidebarNavItemLabelClass, sidebarNavItemLabelActiveClass } from './sidebar-nav-label';
import { sidebarRailIconClass, SIDEBAR_NAV_ICON_SIZE } from './sidebar-nav-icon';
import {
  sidebarNavItemClassName,
  sidebarNavItemActiveClassName,
  sidebarNavItemIconOnlyClassName,
} from './sidebar-nav-item-styles';
import { SidebarNavTooltip } from './sidebar-nav-tooltip';
import { LibraryNavIcon } from './icons/library-nav-icon';
import { SidebarAgentsNavIcon, SidebarHomeNavIcon, SidebarProjectsNavIcon } from './icons/sidebar-rail-icons';

export type SidebarPrimaryNavIcon =
  | 'new-chat'
  | 'group-chat'
  | 'library'
  | 'agents'
  | 'projects'
  | 'knowledge-base'
  | 'integrations'
  | 'tools'
  | 'search'
  | 'scheduled'
  | 'dashboard'
  | 'settings';

const primaryNavIcons = {
  'new-chat': SidebarHomeNavIcon,
  'group-chat': MultiBubble,
  library: LibraryNavIcon,
  agents: SidebarAgentsNavIcon,
  projects: SidebarProjectsNavIcon,
  'knowledge-base': DatabaseStar,
  integrations: Puzzle,
  tools: Tools,
  search: Search,
  scheduled: Clock,
  dashboard: StatsReport,
  settings: Settings,
} as const;

/** Figma rail (9062:222520): home 24, agents 22, everything else 20. */
const railIconSize: Partial<Record<SidebarPrimaryNavIcon, number>> = {
  'new-chat': 24,
  agents: 22,
  library: 20,
  projects: 20,
  'knowledge-base': 20,
  scheduled: 20,
  dashboard: 20,
  tools: 20,
  settings: 20,
};

/** iconoir draws on a 24px grid, so at 20px a 1.8 stroke renders as 1.5px like the custom Figma icons. */
const railStrokeWidth: Partial<Record<SidebarPrimaryNavIcon, number>> = {
  'knowledge-base': 1.8,
  scheduled: 1.8,
  dashboard: 1.8,
  settings: 1.8,
};

interface SidebarPrimaryNavItemProps {
  label: string;
  icon: SidebarPrimaryNavIcon;
  to?: To;
  onClick?: () => void;
  isActive?: boolean;
  disabled?: boolean;
  showText?: boolean;
}

const itemClassName = sidebarNavItemClassName;

export function SidebarPrimaryNavItem({
  label,
  icon,
  to,
  onClick,
  isActive = false,
  disabled = false,
  showText = true,
}: SidebarPrimaryNavItemProps) {
  const Icon = primaryNavIcons[icon];
  const iconSize = railIconSize[icon] ?? SIDEBAR_NAV_ICON_SIZE;

  const content = (
    <>
      <span className="flex size-6 shrink-0 items-center justify-center">
        <Icon
          width={iconSize}
          height={iconSize}
          strokeWidth={railStrokeWidth[icon] ?? 1.5}
          className={sidebarRailIconClass}
        />
      </span>
      {showText && (
        <span className={cn(sidebarNavItemLabelClass, isActive && sidebarNavItemLabelActiveClass, 'truncate')}>{label}</span>
      )}
    </>
  );

  if (to && !disabled) {
    return (
      <SidebarNavTooltip label={label} enabled={!showText}>
        <Link
          to={to}
          onClick={onClick}
          aria-current={isActive ? 'page' : undefined}
          aria-label={!showText ? label : undefined}
          className={cn(
            itemClassName,
            !showText && sidebarNavItemIconOnlyClassName,
            isActive && sidebarNavItemActiveClassName
          )}
        >
          {content}
        </Link>
      </SidebarNavTooltip>
    );
  }

  return (
    <SidebarNavTooltip label={label} enabled={!showText}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={!showText ? label : undefined}
        className={cn(
          itemClassName,
          !showText && sidebarNavItemIconOnlyClassName,
          isActive && sidebarNavItemActiveClassName
        )}
      >
        {content}
      </button>
    </SidebarNavTooltip>
  );
}
